import { json, redirect } from "@remix-run/server-runtime";
import { useState } from "react";
import { Link } from "@remix-run/react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Bell, Zap, ArrowRight, Check, BarChart3, Target, Menu, X
} from "lucide-react";
import SEOHead from "../../components/SEOHead";

const pricingPlans = [
  { name: "Basic", price: "49", features: ["Up to 5 watched competitors", "30 days of change history", "Up to 50 saved ads", "Daily email digest"] },
  { name: "Pro", price: "99", features: ["Up to 20 watched competitors", "90 days of history", "Per-event alerts", "5 Suggested Actions per day"], highlighted: true },
  { name: "Ultra", price: "249", features: ["Up to 100 watched competitors", "365 days of history", "Unlimited saved ads", "15 Suggested Actions per day"] },
];

export const loader = async ({ request }) => {
  const url = new URL(request.url);

  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  return json({});
};

export const meta = () => ([
  { title: "Radar | Competitor Intelligence for Shopify Brands" },
  {
    name: "description",
    content:
      "Track every change your competitors make — new ads, price drops, stock-outs, and product launches — in one dashboard.",
  },
]);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="text-gray-900 font-semibold text-base tracking-tight">Radar</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Features</a>
          <a href="#pricing" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Pricing</a>
          <a href="#faq" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">FAQ</a>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/auth/login">
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900">Log in</Button>
          </Link>
          <Link to="/auth/login">
            <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 font-medium shadow-sm">Get Started</Button>
          </Link>
        </div>
        <button className="md:hidden text-gray-500 hover:text-gray-900" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-6 py-4 space-y-3">
          <a href="#features" className="block text-sm text-gray-500 hover:text-gray-900 py-2">Features</a>
          <a href="#pricing" className="block text-sm text-gray-500 hover:text-gray-900 py-2">Pricing</a>
          <a href="#faq" className="block text-sm text-gray-500 hover:text-gray-900 py-2">FAQ</a>
          <div className="flex gap-3 pt-2">
            <Link to="/auth/login"><Button variant="ghost" size="sm" className="text-gray-500">Log in</Button></Link>
            <Link to="/auth/login"><Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700">Get Started</Button></Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const HeroDashboardMockup = () => (
  <div className="relative w-full max-w-3xl mx-auto mt-12">
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-xl shadow-gray-200/50">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
        </div>
        <span className="text-[11px] text-gray-400 ml-3 font-mono">radar.aquivolabs.com/app</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { label: "Watched competitors", value: "12" },
            { label: "Active ads tracked", value: "142" },
            { label: "Price changes today", value: "8" },
            { label: "New ads this week", value: "23" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1 text-left">{s.label}</div>
              <div className="text-lg text-gray-900 font-semibold text-left">{s.value}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[
            { brand: "Leather Boots Co.", text: "12 active ads · 3 new this week", badge: "Active" },
            { brand: "Denim Jacket Brand", text: "Dropped prices on 5 SKUs", badge: "Alert" },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-left">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-[10px] text-blue-600 font-bold">{item.brand[0]}</div>
                <div>
                  <span className="text-xs text-gray-900 font-medium">{item.brand}</span>
                  <span className="text-xs text-gray-400 ml-2">{item.text}</span>
                </div>
              </div>
              <span className={`text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
                item.badge === "Active" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-orange-50 text-orange-600 border-orange-200"
              }`}>{item.badge}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Radar — Competitor Intelligence for Shopify Brands"
        description="Track every change your competitors make — new ads, price drops, stock-outs, and product launches — in one dashboard."
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center relative">
          <Badge variant="outline" className="mb-6 text-gray-500 border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium">
            Next-Gen E-Commerce Intelligence
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 max-w-3xl mx-auto leading-[1.1]">
            Know what your competitors are doing before it costs you.
          </h1>
          <p className="mt-5 text-base md:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed">
            Radar tracks every change your competitors make — new ads, price drops, stock-outs, product launches — and alerts you when something matters.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <Link to="/auth/login">
              <Button className="bg-blue-600 text-white hover:bg-blue-700 h-10 px-5 text-sm font-medium shadow-sm">
                Connect Your Store <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <HeroDashboardMockup />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 mb-3">See every move your competitors make.</h2>
            <p className="text-sm text-gray-500">Daily syncs, change detection, and email alerts in one Shopify-native app.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: BarChart3, title: "Ad monitoring", desc: "See every Google and Meta ad your competitors are running and how long each one has been live." },
              { icon: Target, title: "Product & price tracking", desc: "Track competitor SKUs, prices, and stock levels across daily syncs." },
              { icon: Bell, title: "Change alerts", desc: "Get email alerts when a competitor drops prices, runs out of stock, or launches a new ad." },
              { icon: Zap, title: "Tech stack detection", desc: "See what Shopify apps, themes, and pixels each competitor runs on their store." },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-gray-300 transition-all text-left">
                <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center mb-4">
                  <f.icon className="w-4 h-4 text-blue-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto text-left">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 mb-3">Simple, transparent pricing.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`rounded-lg p-6 border ${
                plan.highlighted ? "border-blue-200 bg-blue-50/30 relative shadow-sm" : "border-gray-200 bg-white"
              } hover:shadow-md transition-all`}>
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-md shadow-sm">Popular</span>
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-sm text-gray-400">/month</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button className={`w-full h-9 text-sm font-medium ${
                  plan.highlighted ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm" : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`}>Start Free Trial</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-gray-50/50 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center"><Zap className="w-3 h-3 text-white" /></div>
              <span className="text-sm text-gray-900 font-semibold">Radar</span>
            </div>
            <p className="text-xs text-gray-400">2026 Radar Intelligence. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
